import { EnhancePromptTypeEnum } from "@nlc/shared-types";
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";

import { useCapturePosthogEvent } from "@/hooks/posthog/useCapturePosthogEvent";

import type { DetailedFormData } from "../schemas";

import { createOutputEnhancement } from "../../../../shared/createOutputEnhancement";
import { useCreateVideoRequestFormContext } from "../../useCreateVideoRequestForm";

function useAIStream() {
  const capture = useCapturePosthogEvent();
  const methods = useCreateVideoRequestFormContext();
  const abortControllerRef = useRef<AbortController | null>(null);

  const { error, isError, isPending, mutate } = useMutation({
    mutationFn: async ({ data }: { data: DetailedFormData }) => {
      methods.resetField("briefCreatorDetailed.finalBriefHTML");
      methods.resetField("briefCreatorDetailed.finalBrief");
      abortControllerRef.current = new AbortController();

      const response = await createOutputEnhancement(
        {
          brandId: methods.getValues("brand") ?? undefined,
          brief: {
            additionalGuidelines: data.additionalGuidelines,
            brandGuidelines: data.brandGuidelines,
            creativeGoal: data.creativeGoal,
            scope: data.scope,
            script: data.script,
            type: EnhancePromptTypeEnum.Video,
          },
          stream: true,
        },
        abortControllerRef.current,
      );

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        try {
          let accumulatedHTML = "";

          while (true) {
            const { done, value } = await reader.read();

            if (done) break;
            const chunk = decoder.decode(value, { stream: true });

            accumulatedHTML += chunk;

            methods.setValue(
              "briefCreatorDetailed.finalBriefHTML",
              accumulatedHTML,
            );
          }
        } catch (streamError) {
          console.error("Error processing stream:", streamError);
          throw streamError;
        } finally {
          reader.releaseLock();
          capture("magic_brief_generated_completed");
        }
      }

      return response;
    },
    mutationKey: ["createOutputEnhancement", "video"],
  });

  function resetStreamedResponse() {
    methods.setValue("briefCreatorDetailed.finalBriefHTML", "");
  }

  function sendPrompt(data: DetailedFormData) {
    resetStreamedResponse();
    mutate({ data });
  }

  function abortStream() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }

  return {
    abortStream,
    error,
    isError,
    isPending,
    resetStreamedResponse,
    sendPrompt,
    streamedResponse: methods.watch("briefCreatorDetailed.finalBriefHTML"),
  };
}

export default useAIStream;
